import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, money, fmtDate, formatApiError, consumableCategories, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useReportLink, MonthChargesTable } from "@/components/ReportLink";

import { TREATMENTS } from "@/lib/finance";
const empty = () => ({ treatment: null, date: new Date().toISOString().slice(0, 10), apartment_id: "", property_id: "", category: "Consumabili", description: "", amount: 0, attachment_id: "", notes: "", charged_to: "proprietario", considera_rendiconto: true, mostra_pdf: true });

export default function Consumabili() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [linkReport, setLinkReport] = useState(false);
  const { propose, dialog: linkDialog } = useReportLink();
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [fApt, setFApt] = useState("all");
  const [fCat, setFCat] = useState("all");

  const load = async () => {
    const [c, a] = await Promise.all([http.get("/consumables"), http.get("/apartments")]);
    setItems(c.data); setApartments(a.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setForm(empty()); setEditId(null); setOpen(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const p = { ...form, amount: Number(form.amount) || 0 };
    ["apartment_id", "property_id", "attachment_id"].forEach((k) => { if (!p[k]) p[k] = null; });
    try {
      let saved;
      if (editId) saved = (await http.put(`/consumables/${editId}`, p)).data; else saved = (await http.post("/consumables", p)).data;
      toast.success("Salvato"); setOpen(false); load();
      if (saved.apartment_id) {
        const d = new Date(saved.date);
        const req = { apartment_id: saved.apartment_id, year: d.getFullYear(), month: d.getMonth() + 1, category: saved.category === "Amenities" ? "amenities" : "consumabili", amount: saved.amount, source_key: `consumabile:${saved.id}`, label: saved.description || saved.category };
        if (linkReport) propose(req);
        else propose(req, { onlyIfLinked: true, title: "Questa voce era collegata al Rendiconto", soloLabel: "Lascia Rendiconto invariato" });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDelete = async (id) => { if (!window.confirm("Eliminare?")) return; await http.delete(`/consumables/${id}`); load(); };
  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("linked_type", "consumable");
    const { data } = await http.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, attachment_id: data.id })); toast.success("Allegato caricato");
  };
  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "-";
  const filtered = items.filter((i) => (fApt === "all" || i.apartment_id === fApt) && (fCat === "all" || i.category === fCat));
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="consumabili-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Consumabili & Amenities</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Costi per appartamento</h1>
        </div>
        <Button data-testid="new-consumable-btn" onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Nuovo costo</Button>
      </div>

      <MonthChargesTable category="consumabili" testid="charges-consumabili" />

      <Card className="p-4 rounded-2xl flex flex-wrap gap-3 items-end">
        <div><Label>Appartamento</Label>
          <Select value={fApt} onValueChange={setFApt}><SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label>Categoria</Label>
          <Select value={fCat} onValueChange={setFCat}><SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte</SelectItem>{consumableCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="ml-auto text-right"><div className="text-xs uppercase text-slate-500">Totale filtrato</div><div className="font-display text-xl font-extrabold text-[var(--teal-deep)] tabular-nums">{money(total)}</div></div>
      </Card>

      <Card className="rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consumables-table">
            <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
              <th className="py-2 px-4">Data</th><th className="px-2">Appartamento</th><th className="px-2">Categoria</th><th className="px-2">Descrizione</th>
              <th className="px-2 text-right">Importo</th><th className="px-2">A carico</th><th className="px-2"></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">Nessun costo registrato.</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`consumable-${c.id}`}>
                  <td className="py-3 px-4 tabular-nums">{fmtDate(c.date)}</td>
                  <td className="px-2">{aptName(c.apartment_id)}</td>
                  <td className="px-2"><Badge variant="outline" className="border-amber-300 text-amber-800">{c.category}</Badge></td>
                  <td className="px-2">{c.description || "-"}</td>
                  <td className="px-2 text-right tabular-nums font-semibold">{money(c.amount)}</td>
                  <td className="px-2"><Badge variant="outline" className={c.charged_to === "proprietario" ? "border-teal-300 text-teal-800" : "border-slate-300 text-slate-600"}>{c.charged_to === "proprietario" ? "Proprietario" : "PuraVida"}</Badge></td>
                  <td className="px-2"><div className="flex gap-1 justify-end">
                    {c.attachment_id && <a href={fileUrl(c.attachment_id)} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-teal-700"><Paperclip className="h-4 w-4" /></a>}
                    <button onClick={() => { setForm({ ...empty(), ...c }); setEditId(c.id); setOpen(true); }} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => onDelete(c.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica" : "Nuovo"} costo</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4" data-testid="consumable-form">
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
            <div><Label>Appartamento</Label>
              <Select value={form.apartment_id || ""} onValueChange={(v) => { const a = apartments.find((x) => x.id === v); setForm({ ...form, apartment_id: v, property_id: a?.property_id || form.property_id }); }}>
                <SelectTrigger data-testid="cons-apt"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="cons-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{consumableCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cons-amount" /></div>
            <div className="col-span-2"><Label>Descrizione</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Es. cialde caffè x2 confezioni" /></div>
            <div><Label>A carico di</Label>
              <Select value={form.charged_to} onValueChange={(v) => setForm({ ...form, charged_to: v })}><SelectTrigger data-testid="cons-charged"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="proprietario">Proprietario</SelectItem><SelectItem value="puravida">PuraVida Home</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Trattamento cassa PuraVida</Label>
              <Select value={form.treatment || "unset"} onValueChange={(v) => setForm({ ...form, treatment: v === "unset" ? null : v })}><SelectTrigger data-testid="cons-treatment"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unset">Non specificato (nessun movimento)</SelectItem>{TREATMENTS.consumabile.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2 flex items-center gap-3 rounded-lg border border-[color:var(--pv-mint-soft)] bg-[color:var(--teal-light)] p-2.5"><Switch checked={linkReport} onCheckedChange={setLinkReport} data-testid="cons-link-report" /><Label className="text-sm">Aggiorna / addebita nel Rendiconto del mese (anteprima prima di confermare)</Label></div>
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex items-center gap-3"><Switch checked={form.considera_rendiconto} onCheckedChange={(v) => setForm({ ...form, considera_rendiconto: v })} data-testid="cons-considera" /><Label className="text-xs">Considera nel rendiconto</Label></div>
              <div className="flex items-center gap-3"><Switch checked={form.mostra_pdf} onCheckedChange={(v) => setForm({ ...form, mostra_pdf: v })} data-testid="cons-showpdf" /><Label className="text-xs">Mostra nel PDF</Label></div>
            </div>
            <div className="col-span-2"><Label>Allegato (scontrino/fattura)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
              {form.attachment_id && <div className="text-xs text-emerald-600 mt-1">✓ Allegato caricato</div>}
            </div>
            <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="cons-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {linkDialog}
    </div>
  );
}
